import { useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Typography, Box, Stack, Chip, Button, useTheme, alpha } from "@mui/material";
import { useResponsive } from "../../../core/hooks/useResponsive";
import { formatInr, formatSessionDate } from "../ptFormat";
import SessionHistoryCard from "./SessionHistoryCard";

const CARD_PAGE_SIZE = 8;

const SORT_OPTIONS = [
  { key: "newest", label: "Newest" },
  { key: "oldest", label: "Oldest" },
  { key: "netDesc", label: "Net high→low" },
];

const getColorClass = (val1, val2 = 0) => {
  if (val1 > val2) return "profit-cell";
  if (val1 < val2) return "loss-cell";
  return "neutral-cell";
};

const columns = [
  {
    field: "startDate",
    headerName: "Start Date",
    flex: 1.2,
    minWidth: 120,
    valueFormatter: (value) => formatSessionDate(value),
  },
  {
    field: "endDate",
    headerName: "End Date",
    flex: 1.2,
    minWidth: 120,
    valueFormatter: (value) => formatSessionDate(value),
  },
  {
    field: "amountUtilized",
    headerName: "Amount Utilized",
    flex: 1,
    minWidth: 130,
    valueFormatter: (value) => formatInr(value),
  },
  {
    field: "totalProfit",
    headerName: "Total Profit",
    flex: 1,
    minWidth: 120,
    valueFormatter: (value) => formatInr(value),
    cellClassName: (params) => getColorClass(params.value),
  },
  {
    field: "totalLoss",
    headerName: "Total Loss",
    flex: 1,
    minWidth: 120,
    valueFormatter: (value) => formatInr(value),
    cellClassName: (params) => getColorClass(params.value),
  },
  {
    field: "netProfit",
    headerName: "Net Profit",
    flex: 1,
    minWidth: 120,
    valueFormatter: (value) => formatInr(value),
    cellClassName: (params) => getColorClass(params.value),
  },
];

const PaperTradeHistoryGrid = ({ history }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const { isCompact } = useResponsive();
  const [visibleCount, setVisibleCount] = useState(CARD_PAGE_SIZE);
  const [sortKey, setSortKey] = useState("newest");

  const rows = useMemo(() => history || [], [history]);

  const sortedRows = useMemo(() => {
    const out = [...rows];
    if (sortKey === "oldest") out.sort((a, b) => String(a.startDate || "").localeCompare(String(b.startDate || "")));
    else if (sortKey === "netDesc") out.sort((a, b) => (Number(b.netProfit) || 0) - (Number(a.netProfit) || 0));
    else out.sort((a, b) => String(b.startDate || "").localeCompare(String(a.startDate || "")));
    return out;
  }, [rows, sortKey]);

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
        No closed paper trade sessions yet.
      </Typography>
    );
  }

  if (isCompact) {
    return (
      <Box>
        {rows.length > 1 && (
          <Stack direction="row" spacing={0.75} sx={{ mb: 1, overflowX: "auto", pb: 0.25 }}>
            {SORT_OPTIONS.map((opt) => (
              <Chip
                key={opt.key}
                label={opt.label}
                size="small"
                onClick={() => setSortKey(opt.key)}
                variant={sortKey === opt.key ? "filled" : "outlined"}
                color={sortKey === opt.key ? "primary" : "default"}
                sx={{ fontWeight: 700, fontSize: "0.7rem", flexShrink: 0 }}
              />
            ))}
          </Stack>
        )}
        <Stack spacing={1}>
          {sortedRows.slice(0, visibleCount).map((row) => (
            <SessionHistoryCard key={row.id} row={row} />
          ))}
        </Stack>
        {visibleCount < sortedRows.length && (
          <Button
            fullWidth
            size="small"
            onClick={() => setVisibleCount((c) => c + CARD_PAGE_SIZE)}
            sx={{ mt: 1, textTransform: "none", fontWeight: 700 }}
          >
            Show {Math.min(CARD_PAGE_SIZE, sortedRows.length - visibleCount)} more
            {` (${visibleCount} of ${sortedRows.length})`}
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: 520,
        "& .MuiDataGrid-columnHeaders": {
          bgcolor: alpha(primary, 0.06),
          color: primary,
        },
        "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 800, fontSize: "0.75rem", letterSpacing: "0.03em" },
        "& .MuiDataGrid-row:hover": { bgcolor: alpha(primary, 0.06) },
        "& .profit-cell": { color: theme.palette.success?.main || "#16a34a", fontWeight: 700 },
        "& .loss-cell": { color: theme.palette.error?.main || "#dc2626", fontWeight: 700 },
        "& .neutral-cell": { color: theme.palette.text.secondary },
      }}
    >
      <DataGrid
        rows={rows}
        columns={columns}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
          sorting: { sortModel: [{ field: "startDate", sort: "desc" }] },
        }}
        pageSizeOptions={[25, 50, 100]}
        getRowId={(row) => row.id}
        rowHeight={36}
        disableRowSelectionOnClick
      />
    </Box>
  );
};

export default PaperTradeHistoryGrid;
